import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { menusAPI } from '../utils/api';

// Module-level cache to avoid re-fetching menus on every hook mount
let _menuRutaMap = null;
let _menuMapPromise = null;

function getMenuRutaMap() {
  if (_menuRutaMap) return Promise.resolve(_menuRutaMap);
  if (_menuMapPromise) return _menuMapPromise;
  _menuMapPromise = menusAPI.getAll().then(res => {
    const menus = res.data || [];
    const map = {};
    menus.forEach(m => {
      if (m.ruta && m.id) map[m.ruta] = m.id;
    });
    _menuRutaMap = map;
    return map;
  }).catch(() => {
    _menuMapPromise = null;
    return {};
  });
  return _menuMapPromise;
}

/**
 * Hook para verificar permisos del usuario actual
 * Resuelve ruta → menu_id y compara contra user.permisos
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const [rutaMap, setRutaMap] = useState(_menuRutaMap || {});
  const loaded = useRef(false);

  useEffect(() => {
    if (!loaded.current) {
      loaded.current = true;
      getMenuRutaMap().then(map => setRutaMap(map));
    }
  }, []);

  const hasPermission = (menuRoute, action) => {
    if (user?.rol_nombre === 'Administrador') return true;
    if (!user?.permisos || user.permisos.length === 0) return true;

    const menuId = rutaMap[menuRoute];
    if (!menuId) return false;

    const permiso = user.permisos.find(p => p.menu_id === menuId);
    return permiso ? !!permiso[action] : false;
  };

  const canView = (menuRoute) => hasPermission(menuRoute, 'ver');
  const canCreate = (menuRoute) => hasPermission(menuRoute, 'crear');
  const canEdit = (menuRoute) => hasPermission(menuRoute, 'editar');
  const canDelete = (menuRoute) => hasPermission(menuRoute, 'eliminar');
  const isAdmin = () => user?.rol_nombre === 'Administrador';

  return {
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    isAdmin,
    loading: false,
    permissions: {}
  };
};

export default usePermissions;
